import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { SERVICES, CAPABILITY_MODELS, ENGAGEMENT_MODELS, BUILD_MODELS, PODS } from "../lib/data";
import { Check, ArrowUpRight, Users, CalendarDays, Sparkles, Wallet, PiggyBank, Zap, DollarSign, Clock, TrendingDown } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { motion } from "framer-motion";
import { Counter } from "../components/Counter";

const Estimator = () => {
  const [engineers, setEngineers] = useState([6]);
  const [months, setMonths] = useState([6]);
  const [aiBoost, setAiBoost] = useState([35]);
  const [rate, setRate] = useState([45]); // hourly blended rate USD

  const d = useMemo(() => {
    const e = engineers[0], m = months[0], boost = aiBoost[0] / 100, hr = rate[0];
    const HRS_PER_MONTH = 160; // 40h/wk × 4 wks
    const WKS_PER_MONTH = 4.33;
    const efficiency = 1 - boost * 0.55; // 55% of AI boost converts to real reduction

    const tradCost = Math.round(e * m * HRS_PER_MONTH * hr);
    const tradWeeks = Math.round(m * WKS_PER_MONTH);

    const zCost = Math.round(tradCost * efficiency);
    const zWeeks = Math.max(1, Math.round(tradWeeks * efficiency));

    const moneySaved = tradCost - zCost;
    const weeksSaved = tradWeeks - zWeeks;
    const pctCost = Math.round((moneySaved / tradCost) * 100);
    const pctTime = Math.round((weeksSaved / tradWeeks) * 100);
    const zPctWidth = (zCost / tradCost) * 100;

    return { tradCost, tradWeeks, zCost, zWeeks, moneySaved, weeksSaved, pctCost, pctTime, zPctWidth };
  }, [engineers, months, aiBoost, rate]);

  const fmt = (n) => "$" + n.toLocaleString();

  const controls = [
    { label: "Engineering team size", val: engineers, set: setEngineers, min: 2, max: 40, unit: "engineers", icon: Users, tid: "slider-engineers" },
    { label: "Program duration", val: months, set: setMonths, min: 1, max: 24, unit: "months", icon: CalendarDays, tid: "slider-months" },
    { label: "Blended hourly rate (USD)", val: rate, set: setRate, min: 20, max: 200, unit: "/hr", icon: DollarSign, tid: "slider-rate", prefix: "$" },
    { label: "AI acceleration", val: aiBoost, set: setAiBoost, min: 10, max: 70, unit: "%", icon: Sparkles, tid: "slider-aiboost" },
  ];

  return (
    <section id="estimator" className="relative overflow-hidden border-y border-border bg-navy py-24 text-white grain" data-testid="estimator">
      <div className="pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-teal/20 blur-[120px]" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full" style={{ background: "radial-gradient(circle,rgba(245,197,66,0.18),transparent 70%)" }} />
      <div className="container-x relative">
        <Reveal className="mb-12 max-w-2xl">
          <p className="overline text-gradient-bright">ROI Calculator</p>
          <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
            ROI Calculator — see your AI-accelerated return in real time.
          </h2>
          <p className="mt-4 text-white/60">Set your team size, duration, blended hourly rate and AI acceleration. Compare Traditional vs. Zenspark cost and timeline — with a summary of money and time saved.</p>
        </Reveal>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Controls */}
          <div className="lg:col-span-2 space-y-7 rounded-2xl border border-white/10 bg-white/5 p-8">
            {controls.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} data-testid={`${s.tid}-group`}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-white/70"><Icon className="h-4 w-4 text-accent" /> {s.label}</span>
                    <span className="font-display text-lg font-semibold text-gold">
                      {s.prefix || ""}{s.val[0]}
                      <span className="ml-1 text-xs font-normal uppercase tracking-wider text-white/50">{s.unit}</span>
                    </span>
                  </div>
                  <Slider data-testid={s.tid} value={s.val} onValueChange={s.set} min={s.min} max={s.max} step={1} />
                  <div className="mt-1.5 flex justify-between font-mono text-[10px] uppercase tracking-wider text-white/30">
                    <span>{s.prefix || ""}{s.min}</span><span>{s.prefix || ""}{s.max}</span>
                  </div>
                </div>
              );
            })}
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-accent">How it's calculated</p>
              <p className="mt-2 text-xs leading-relaxed text-white/50">
                Traditional cost = team × duration × 160 hrs/month × hourly rate. Timeline = duration × 4.33 weeks.
                AI acceleration reduces effort at ~55% conversion — applied to both cost and timeline on the Zenspark model.
              </p>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-3 space-y-6">
            {/* Side-by-side model comparison */}
            <div className="grid gap-4 sm:grid-cols-2" data-testid="model-comparison">
              {/* Traditional */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Traditional delivery</p>
                <div className="mt-4 flex items-start gap-3">
                  <Wallet className="mt-1 h-5 w-5 text-white/60" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Cost</p>
                    <p data-testid="trad-cost" className="font-display text-2xl font-semibold text-white">{fmt(d.tradCost)}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <Clock className="mt-1 h-5 w-5 text-white/60" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Timeline</p>
                    <p data-testid="trad-weeks" className="font-display text-2xl font-semibold text-white">{d.tradWeeks} wks</p>
                  </div>
                </div>
                <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="h-full w-full rounded-full bg-white/25" />
                </div>
              </div>

              {/* Zenspark */}
              <div className="relative rounded-2xl border border-accent/40 bg-gradient-to-br from-teal/10 to-gold/5 p-6">
                <span className="absolute right-4 top-4 rounded-full bg-accent/20 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent">Zenspark</span>
                <p className="font-mono text-[10px] uppercase tracking-widest text-accent">With Zenspark AI</p>
                <div className="mt-4 flex items-start gap-3">
                  <Wallet className="mt-1 h-5 w-5 text-accent" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Cost</p>
                    <p data-testid="zenspark-cost" className="font-display text-2xl font-semibold text-gradient-bright">{fmt(d.zCost)}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <Clock className="mt-1 h-5 w-5 text-accent" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Timeline</p>
                    <p data-testid="zenspark-weeks" className="font-display text-2xl font-semibold text-gradient-bright">{d.zWeeks} wks</p>
                  </div>
                </div>
                <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundImage: "linear-gradient(90deg,#0E8F9E,#29B3C2,#F5C542)" }}
                    animate={{ width: `${d.zPctWidth}%` }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              </div>
            </div>

            {/* Savings summary */}
            <div className="rounded-2xl border border-gold/30 bg-gradient-to-br from-navy/60 to-gold/10 p-6" data-testid="savings-summary">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-sm text-white/70">
                  <TrendingDown className="h-4 w-4 text-gold" />
                  With Zenspark's AI-native model, you save
                </p>
                <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold">Summary</span>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-2">
                    <PiggyBank className="h-4 w-4 text-accent" />
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Money saved</p>
                  </div>
                  <p data-testid="money-saved" className="mt-2 font-display text-4xl font-semibold text-gradient-bright">
                    $<Counter to={d.moneySaved} format="comma" />
                  </p>
                  <p className="mt-1 text-xs text-white/50">{d.pctCost}% lower than traditional</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-accent" />
                    <p className="text-[11px] uppercase tracking-wider text-white/50">Time saved</p>
                  </div>
                  <p data-testid="time-saved" className="mt-2 font-display text-4xl font-semibold text-gradient-bright">
                    {d.weeksSaved} <span className="text-2xl">wks</span>
                  </p>
                  <p className="mt-1 text-xs text-white/50">{d.pctTime}% faster to production</p>
                </div>
              </div>

              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-full brand-gradient animate-gradient px-6 py-3 text-sm font-semibold brand-glow transition-transform hover:scale-[1.03]">
                Get a precise quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Services() {
  return (
    <>
      <PageHero
        testid="services-hero"
        overline="Engineering Intelligence Services"
        title="Ten practice areas. One delivery discipline."
        subtitle="Every engagement led by a specialist who has spent a career in the domain — following one lifecycle: Discover → Design → Deploy → Operate."
      />

      <section className="bg-background py-24">
        <div className="container-x space-y-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} id={s.slug}>
                <div
                  data-testid={`service-detail-${s.slug}`}
                  className="scroll-mt-28 grid gap-8 rounded-2xl border border-border bg-background p-8 md:grid-cols-12 md:p-12"
                >
                  <div className="md:col-span-5">
                    <span className="grid h-14 w-14 place-items-center rounded-xl brand-gradient text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{s.tag}</p>
                    <h2 className="mt-2 text-3xl font-semibold tracking-tighter">{s.title}</h2>
                    <p className="mt-4 leading-relaxed text-muted-foreground">{s.desc}</p>
                  </div>
                  <div className="md:col-span-7 md:border-l md:border-border md:pl-12">
                    <div className="grid gap-4 sm:grid-cols-2">
                      {s.points.map((p) => (
                        <div key={p} className="flex items-start gap-3">
                          <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          <span className="text-sm leading-relaxed text-foreground/90">{p}</span>
                        </div>
                      ))}
                    </div>
                    <Link to={`/services/${s.slug}`} className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                      Explore {s.title} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40 py-24" data-testid="capability-models">
        <div className="container-x">
          <Reveal className="mb-12 max-w-2xl">
            <p className="overline text-gradient">Global Engineering Capability</p>
            <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              Flexible models, integrated into your teams.
            </h2>
            <p className="mt-4 text-muted-foreground">Technology alone doesn't create transformation. Exceptional teams do.</p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITY_MODELS.map((m, i) => (
              <Reveal key={m.name} delay={(i % 3) * 0.06} className="group rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:bg-secondary/60">
                <span className="font-mono text-sm text-accent">0{i + 1}</span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{m.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mb-8 mt-20 max-w-2xl">
            <p className="overline text-gradient">Engagement Models</p>
            <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              Pick the deal shape that matches your risk.
            </h2>
            <p className="mt-4 text-muted-foreground">Eight models, one operating discipline. Every first engagement begins with a 45-minute scoping conversation — we recommend the model, you decide.</p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ENGAGEMENT_MODELS.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.05} className="rounded-2xl border border-border bg-background p-6">
                <h3 className="text-base font-semibold tracking-tight">{m.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mb-8 mt-20 max-w-2xl" id="build-models">
            <p className="overline text-gradient">Build Models</p>
            <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              However you need to build, we build it.
            </h2>
            <p className="mt-4 text-muted-foreground">Every build model ships with CI/CD, peer review, security scanning, infrastructure as code and production observability.</p>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {BUILD_MODELS.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.06} className="flex flex-col rounded-2xl border border-border bg-background p-8">
                <span className="font-mono text-sm text-accent">0{i + 1}</span>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{m.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                <span className="mt-5 inline-flex w-fit rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">{m.fit}</span>
              </Reveal>
            ))}
          </div>

          <Reveal className="mb-8 mt-20 max-w-2xl" id="pods">
            <p className="overline text-gradient">Extendable PODs</p>
            <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              Pre-formed teams. Deployed fast.
            </h2>
            <p className="mt-4 text-muted-foreground">Cross-functional, AI-enabled PODs with a defined composition and a typical time-to-deploy (TAT). Add or remove roles without renegotiating the engagement.</p>
          </Reveal>
          <Reveal className="overflow-hidden rounded-2xl border border-border bg-background">
            {/* header row */}
            <div className="hidden grid-cols-12 gap-4 border-b border-border bg-secondary/60 px-6 py-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:grid">
              <span className="col-span-4">POD</span>
              <span className="col-span-6">Typical composition</span>
              <span className="col-span-2 text-right">Time to deploy</span>
            </div>
            {PODS.map((p) => (
              <div key={p.name} className="grid grid-cols-1 gap-2 border-b border-border px-6 py-5 transition-colors last:border-0 hover:bg-secondary/40 sm:grid-cols-12 sm:gap-4">
                <div className="col-span-4 flex items-center gap-3">
                  <span className="h-2 w-2 shrink-0 rounded-full brand-gradient" />
                  <span className="font-semibold tracking-tight">{p.name}</span>
                </div>
                <span className="col-span-6 text-sm text-muted-foreground">{p.team}</span>
                <span className="col-span-2 sm:text-right">
                  <span className="inline-flex rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">{p.tat}</span>
                </span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <Estimator />
    </>
  );
}
